import {
  ListSubheader,
  MenuItem,
  TextField,
  TextFieldProps,
} from "@fxc/ui-kit";
import {
  Control,
  ControllerProps,
  FieldError,
  FieldValues,
  Path,
  useController,
} from "react-hook-form";

export type SelectElementOption = {
  id: string | number;
  label: string | number;
  group?: boolean;
};

export type SelectElementProps<T extends FieldValues> = Omit<
  TextFieldProps,
  "name" | "type" | "onChange"
> & {
  validation?: ControllerProps["rules"];
  name: Path<T>;
  options?: SelectElementOption[] | any[];
  valueKey?: string;
  labelKey?: string;
  type?: "string" | "number";
  parseError?: (error: FieldError) => string;
  objectOnChange?: boolean;
  onChange?: (value: any) => void;
  control?: Control<T>;
};

export default function SelectElement<TFieldValues extends FieldValues>({
  name,
  required,
  valueKey = "id",
  labelKey = "label",
  options = [],
  parseError,
  type,
  objectOnChange,
  validation = {},
  control: _control,
  ...rest
}: SelectElementProps<TFieldValues>): JSX.Element {
  const isNativeSelect = !!(rest.slotProps?.select as any)?.native;
  const ChildComponent = isNativeSelect ? "option" : MenuItem;
  const GroupComponent = isNativeSelect ? "optgroup" : ListSubheader;

  if (required && !validation.required) {
    validation.required = "This field is required";
  }

  const {
    field: { onBlur, onChange },
    field,
    fieldState: { invalid, error },
  } = useController({ name, rules: validation });

  let value = field.value;

  // handle shrink on number input fields
  if (type === "number" && typeof value !== "undefined") {
    rest.slotProps = rest.slotProps || {};
    rest.slotProps.inputLabel = {
      ...(rest.slotProps.inputLabel as object),
      shrink: true,
    };
  }
  if (typeof value === "object" && value) {
    value = value[valueKey]; // if value is object get key
  }
  return (
    <TextField
      {...rest}
      name={name}
      value={value ?? ""}
      onBlur={onBlur}
      onChange={(event) => {
        let item: number | string = event.target.value;
        if (type === "number") {
          item = Number(item);
        }
        onChange(item);
        if (typeof rest.onChange === "function") {
          if (objectOnChange) {
            item = options.find((i) => i[valueKey] === item);
          }
          rest.onChange(item);
        }
      }}
      select
      required={required}
      error={invalid}
      helperText={
        error
          ? typeof parseError === "function"
            ? parseError(error)
            : error.message
          : rest.helperText
      }
    >
      {isNativeSelect && <option />}
      {options.map((item: any) =>
        item.group ? (
          <GroupComponent key={`${name}_${item[valueKey]}`}>
            {item[labelKey]}
          </GroupComponent>
        ) : (
          <ChildComponent
            key={`${name}_${item[valueKey]}`}
            value={item[valueKey]}
          >
            {item[labelKey]}
          </ChildComponent>
        ),
      )}
    </TextField>
  );
}
